import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IEntityColumn, IEntityToken, ITriggerAction, IUserConfigPackage, TriggerActionType } from '../../../API';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { Dropdown, Stack, TextField } from '@fluentui/react';
import TiptapEditor from '../../controls/TiptapEditor';
import NewPeoplePicker, { PeoplePickerIdFormat } from '../../controls/NewPeoplePicker';
import TokenPathTagPicker, { TokenPathTagPickerState } from '../../controls/TokenPathTagPicker';

export class TriggerActionEditorState implements ITriggerAction {
	public userConfig: IUserConfigPackage;

	public dialog:ReusableDialogState;

	public Id: string = '';

	public ActionType:TriggerActionType = TriggerActionType.SetField;
	public ActionArgs:string = '';

	public listUrl: string = '';
	public availableColumns?: IEntityColumn[];
	public loadingColumns: boolean = false;
	
	// Fields for Set Field action type
	public fieldInternalName: string = '';
	public fieldValue: string = '';

	// Fields for Send Email action type
	public emailFrom: string[] = [];
	public emailTo: string = '';
	public emailSubject: string = '';
	public emailBody: string = '';

	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	original: ITriggerAction = {} as ITriggerAction;
	//-------------

	public tokens?: IEntityToken[];

	private thisItem() : ITriggerAction {
		return {
			Id: this.Id,
			ActionType: this.ActionType,
			ActionArgs: this.ActionArgs,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl
		};
	}


	public dialogButtons: IReusableDialogFooterProps = {
		leftButtons: [/*
			{
				text: 'Delete',
				iconProps: {iconName: 'Delete'},
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onDelete(this.original);
				},
			}
		*/],
		rightButtons: [
			{
				text: 'Cancel',
				onClick: (ev) => {
					this.dialog.onDismiss();
				},
			},
			{
				text: 'Save',
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onSave(this.original, this.thisItem());
				},
			}
		],
	};

	onSave: (original:ITriggerAction, updated:ITriggerAction) => void;
	onDelete: (profile:ITriggerAction) => void;

	constructor(
		userConfig: IUserConfigPackage,
		onDelete: (profile:ITriggerAction) => void,
		onSave: (original:ITriggerAction, updated:ITriggerAction) => void,
		listUrl: string = ''
	) {
		this.userConfig = userConfig;
		this.onDelete = onDelete;
		this.onSave = onSave;
		this.listUrl = listUrl;
		
		this.dialog = new ReusableDialogState("Trigger Action", 500);

		makeAutoObservable(this);
	}

	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
	}

	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
	}

	setAvailableFields(cols: IEntityColumn[]) {
		this.availableColumns = cols;
	}

	public setItem(item:ITriggerAction) {
		this.original = item;
		Object.assign(this, item);
		
		// Parse ActionArgs if it's Set Field type
		if (item.ActionType === TriggerActionType.SetField && item.ActionArgs) {
			try {
				const args = JSON.parse(item.ActionArgs);
				this.fieldInternalName = args.FieldInternalName || '';
				this.fieldValue = args.FieldValue || '';
			} catch (error) {
				console.error('Error parsing ActionArgs:', error);
				// Clear both field values and ActionArgs on parse failure
				this.fieldInternalName = '';
				this.fieldValue = '';
				this.ActionArgs = '';
			}
		} else if (item.ActionType === TriggerActionType.SendEmail && item.ActionArgs) {
			try {
				const args = JSON.parse(item.ActionArgs);
				this.emailFrom = args.From ? [args.From] : [];
				this.emailTo = Array.isArray(args.To) ? args.To.join(', ') : (args.To || '');
				this.emailSubject = args.Subject || '';
				this.emailBody = args.Body || '';
			} catch (error) {
				console.error('Error parsing ActionArgs:', error);
				// Clear email field values and ActionArgs on parse failure
				this.emailFrom = [];
				this.emailTo = '';
				this.emailSubject = '';
				this.emailBody = '';
				this.ActionArgs = '';
			}
		} else {
			// Clear field values when ActionType doesn't match
			this.fieldInternalName = '';
			this.fieldValue = '';
			this.emailFrom = [];
			this.emailTo = '';
			this.emailSubject = '';
			this.emailBody = '';
		}
		
	}
	
	public setActionType(val:TriggerActionType) {
		this.ActionType = val;
	}

	public setFieldInternalName(val: string) {
		this.fieldInternalName = val;
		this.updateActionArgsFromFields();
	}

	public setFieldValue(val: string) {
		this.fieldValue = val;
		this.updateActionArgsFromFields();
	}

	private updateActionArgsFromFields() {
		if (this.ActionType === TriggerActionType.SetField) {
			const args = {
				FieldInternalName: this.fieldInternalName,
				FieldValue: this.fieldValue
			};
			this.ActionArgs = JSON.stringify(args);
		} else if (this.ActionType === TriggerActionType.SendEmail) {
			const toArray = (this.emailTo || '').split(',').map(e => e.trim()).filter(e => e);
			const args = {
				From: this.emailFrom.length > 0 ? this.emailFrom[0] : '',
				To: toArray,
				Subject: this.emailSubject || '',
				Body: this.emailBody || ''
			};
			this.ActionArgs = JSON.stringify(args);
		}
	}

	public setEmailFrom(val: string[]) {
		this.emailFrom = val;
		this.updateActionArgsFromFields();
	}

	public setEmailTo(val: string) {
		this.emailTo = val;
		this.updateActionArgsFromFields();
	}

	public setEmailSubject(val: string) {
		this.emailSubject = val;
		this.updateActionArgsFromFields();
	}

	public setEmailBody(val: string) {
		this.emailBody = val;
		this.updateActionArgsFromFields();
	}

	public setActionArgs(val:string) {
		this.ActionArgs = val;
		
		// Parse and update field values when manually editing JSON for Set Field action
		if (this.ActionType === TriggerActionType.SetField && val) {
			try {
				const args = JSON.parse(val);
				this.fieldInternalName = args.FieldInternalName || '';
				this.fieldValue = args.FieldValue || '';
			} catch (error) {
				// Invalid JSON - keep as is
			}
		}
	}
}

function TriggerActionEditor({
	state
}: {
	state: TriggerActionEditorState
}) {

	const fieldDropdownOptions = (state.availableColumns ?? []).map((c) => {
		return {
			key: c.id,
			text: `${c.displayName} (${c.id})`,
		};
	}).sort((a,b) => (a.text || '').toLowerCase().localeCompare((b.text || '').toLowerCase()));

	// Use the stored field internal name as the selected key
	const selectedFieldKey = state.fieldInternalName || '';
	
	// Determine if the field dropdown should be disabled
	const isFieldDropdownDisabled = !state.availableColumns || state.availableColumns.length === 0 || state.loadingColumns;

	return (

		<ReusableDialog state={state.dialog} footerButtons={state.dialogButtons}>
			
			<Stack>

				<InfoTooltipLabel label='Action Type' title='Select the type of action to perform when the trigger conditions are met.'>
					<Dropdown options={[
						{ key: TriggerActionType.SetField, text: 'Set Field' },
						{ key: TriggerActionType.SendEmail, text: 'Send Email' }
					]} defaultSelectedKey={state.ActionType} onChange={(e, option) => state.setActionType(option?.key as TriggerActionType)} />
				</InfoTooltipLabel>

				{state.ActionType === TriggerActionType.SetField && (
					<>
						<InfoTooltipLabel label='Field' title='Select the field to set when the trigger conditions are met.'>
							<Dropdown
								disabled={isFieldDropdownDisabled}
								options={fieldDropdownOptions}
								selectedKey={selectedFieldKey}
								onChange={(e, option) => {
									state.setFieldInternalName(option?.key as string);
								}}
								placeholder={state.loadingColumns ? 'Loading fields...' : 'Select a field'}
							/>
						</InfoTooltipLabel>

						<InfoTooltipLabel label='Field Value' title='Enter the value to set for the selected field.'>
							<TokenPathTagPicker
								state={new TokenPathTagPickerState(state.setFieldValue.bind(state), '', undefined, state.fieldValue, state.tokens ?? [])}
							/>
						</InfoTooltipLabel>
					</>
				)}

				{state.ActionType === TriggerActionType.SendEmail && (
					<>
						<InfoTooltipLabel label='From' title='Select the user to send the email from. The user must have permission to send emails.'>
							<NewPeoplePicker 
								format={PeoplePickerIdFormat.UPN}
								siteUrl={state.listUrl} 
								config={state.userConfig} 
								initialPeople={state.emailFrom} 
								updatePeople={(people) => state.setEmailFrom(people)} 
							/>
						</InfoTooltipLabel>

						<InfoTooltipLabel label='To' title='Enter recipient email addresses, separated by commas (e.g., user1@domain.com, user2@domain.com)'>
							<TextField value={state.emailTo} onChange={(e, v) => state.setEmailTo(v || '')} placeholder="Email addresses or people field placeholders e.g. {{Item.AssignedTo}}" />
						</InfoTooltipLabel>

						<InfoTooltipLabel label='Subject' title='Enter the email subject.'>
							<TextField value={state.emailSubject} onChange={(e, v) => state.setEmailSubject(v || '')} placeholder="Email subject" />
						</InfoTooltipLabel>

						<InfoTooltipLabel label='Body' title='Enter the email body content using the rich text editor.'>
							<TiptapEditor
								value={state.emailBody}
								onChange={(content) => state.setEmailBody(content)}
							/>
						</InfoTooltipLabel>
					</>
				)}
			</Stack>
		</ReusableDialog>
	);
}

export default observer(TriggerActionEditor);
